/**
 * The Bar: drinks are the only power-ups in the game, and the Buzz meter is
 * what they cost besides money. Every drink is general or economy — none is
 * tied to a particular game.
 *
 * Buzz 0-10. Each drink adds Buzz; it drops by 1 every SOBER_EVERY plays,
 * and Water/Coffee drop it faster. Stages:
 *   0-3 Sober · 4-6 Tipsy (no downside; Happy Drunk needs it)
 *   7-9 Drunk: the bouncer keeps you off the floor's two priciest tables,
 *       10% of each win goes to a tip, you sometimes drop a chip, and
 *       drinks cost 50% more
 *   10 Blackout: the run is over.
 * Drunk penalties are rule changes announced as text — never visual tricks
 * on the lens (direct design decision).
 */

export const MAX_BUZZ = 10;
export const DRUNK_AT = 7;
export const TIPSY_AT = 4;
/** Buzz drops by one after this many settled plays. Was 4; 5 from
 *  2026-09-25 (user) to stay in the Tipsy range longer. */
export const SOBER_EVERY = 5;
/** Drinks that last a while (or wait for their moment) take a slot. */
export const DRINK_SLOTS = 3;

/**
 * Drink strengths and the repeat-order rule, in one place so the balance
 * sims can try variants. The game reads these; only sims change them.
 * Set 2026-09-23 after the drink audit found the game ran on Lucky Lager
 * bought before every hand: the reorder cooldown ends that, and the timed
 * drinks were doubled to carry the weight instead (retune.sim.test.ts).
 */
export const TUNING = {
  /** Plays before the bartender pours the same drink again, when
   *  restockFactor is 0 (the old flat rule). */
  reorderCooldown: 5,
  /** Sold out (user, 2026-09-23): a drink restocks this many times its
   *  duration after it's poured (rounded), so a 10-play drink comes back 5
   *  plays after it runs out. One-shots count as lasting 5; Water 5 plays,
   *  Coffee 10 (see restockOf). 0 = the flat reorderCooldown for everything.
   *  Tried 2 (fresh runs 31%, all upgrades 54%) and 1.5; after Designated
   *  Driver was cut and the floors re-shuffled, the user picked 1.75 (the ★
   *  beats blackjack-only; fresh 36%, all upgrades 66%). */
  restockFactor: 1.75,
  /** Espresso Martini: share of every payout added. */
  espresso: 0.1,
  /** Cashback Cosmo: share of every loss given back. */
  cosmo: 0.2,
  /** Happy Drunk: share of every payout added while Tipsy or worse. */
  happy: 0.2,
  /** House Pour: each win pays this share of the table minimum extra.
   *  Raised from 0.5 (user, 2026-09-23: it felt too weak). */
  pour: 1,
  /** Top Shelf: share of every win's winnings added. */
  topShelf: 0.5,
};

export type DrinkId =
  | 'water'
  | 'coffee'
  | 'espresso'
  | 'lager'
  | 'pour'
  | 'cosmo'
  | 'happy'
  | 'lastcall'
  | 'topshelf';

export interface DrinkDef {
  id: DrinkId;
  name: string;
  /** Shorter name for the Bar list when "name $price" won't fit the
   *  25-character left pane (Penthouse prices run to five characters). */
  short?: string;
  /** Shown in the Bar's right pane. */
  effect: string;
  /** Base price in floor-1 dollars, times the floor's bet multiplier. */
  basePrice: number;
  /** Buzz added (negative sobers you up). */
  buzz: number;
  /** Plays the effect lasts, 'once' for a one-shot that waits for its
   *  moment, or 'instant' for drinks that act right away and take no slot. */
  lasts: number | 'once' | 'instant';
  /** First floor whose bar pours it. */
  floor: number;
  /** Goes on top of your drinks instead of taking a slot. */
  slotless?: boolean;
}

export const DRINKS: DrinkDef[] = [
  { id: 'water', name: 'Water', effect: 'Sobers you up a little.', basePrice: 1, buzz: -1, lasts: 'instant', floor: 1 },
  { id: 'coffee', name: 'Coffee', effect: 'Sobers you up.', basePrice: 3, buzz: -2, lasts: 'instant', floor: 1 },
  { id: 'espresso', name: 'Espresso Martini', effect: `Every payout +${TUNING.espresso * 100}% (at least $1) for 10 plays.`, basePrice: 5, buzz: 1, lasts: 10, floor: 2 },
  { id: 'lager', name: 'Lucky Lager', effect: 'Your next losing play is refunded.', basePrice: 4, buzz: 1, lasts: 'once', floor: 1 },
  // Replaced Jackpot Julep and Special Sour (2026-09-23 drink audit: worth
  // ~nothing) with the two missing roles: a Main Floor starter and a
  // swing-reducer.
  { id: 'pour', name: 'House Pour', effect: 'Wins pay the table minimum extra for 10 plays.', basePrice: 2, buzz: 1, lasts: 10, floor: 1 },
  { id: 'cosmo', name: 'Cashback Cosmo', effect: `${TUNING.cosmo * 100}% of every loss back for 15 plays.`, basePrice: 6, buzz: 2, lasts: 15, floor: 1 },
  // Drink audit (user, 2026-09-23): Hot Streak Highball, Shandy, Old
  // Fashioned and Liquid Courage were cut (net negative or no effect on
  // runs); Top Shelf was added, and Designated Driver was added then cut (it
  // cost ~10 points of win rate by taking a slot). Floors re-shuffled the
  // same day (user): each drink sits where it's most useful, one new drink
  // on every floor above the Main Floor. Lucky Star (more luck at the ★) was
  // cut once Liquid Courage (Gift Shop) gave that luck for being Tipsy, and
  // Cashback Cosmo moved down to the Main Floor in its place (user).
  { id: 'happy', name: 'Happy Drunk', effect: `Payouts +${TUNING.happy * 100}% while you are Tipsy or worse, for 10 plays.`, basePrice: 6, buzz: 2, lasts: 10, floor: 3 },
  { id: 'lastcall', name: 'Last Call', effect: 'Your next play pays 3x its winnings if it wins.', basePrice: 10, buzz: 3, lasts: 'once', floor: 4 },
  // The Penthouse closer (user, 2026-09-23: the last drink unlocked should
  // help push you over the edge): no slot, so it stacks on Lager/Pour/Cosmo.
  { id: 'topshelf', name: 'Top Shelf', effect: `Welcome to the top. Every win pays +${TUNING.topShelf * 100}% of its winnings for 10 plays. Needs no slot.`, basePrice: 8, buzz: 2, lasts: 10, floor: 5, slotless: true },
];

export function drinkDef(id: DrinkId): DrinkDef {
  return DRINKS.find((d) => d.id === id)!;
}

export interface ActiveDrink {
  id: DrinkId;
  /** Plays left for timed drinks; null for one-shots. */
  playsLeft: number | null;
}

export interface BarState {
  buzz: number;
  /** Settled plays since Buzz last dropped. */
  soberProgress: number;
  drinks: ActiveDrink[];
  /** Winning plays in a row (pushes don't break it). */
  winStreak: number;
  /** Buzz that blacks you out (10). */
  maxBuzz: number;
  /** Buzz where Tipsy starts (4, or 3 with Liquid Courage II). */
  tipsyAt?: number;
  /** Buzz where Drunk starts (7; 8 with Tolerance I, 9 with Tolerance II). */
  drunkAt?: number;
  /** Taken off the restock factor (Booger Sugar: 0.25 a tier). */
  restockCut?: number;
  /** Settled plays per point of Buzz lost (4, or 3 with Fast Metabolism). */
  soberEvery: number;
  /** Drinks that can be going at once (3, or 4 with Drink Slot +1). */
  slots: number;
  /** Multiplier on drink prices (1, or 0.75 with Happy Hour). */
  priceFactor: number;
  /** Plays until the bartender will pour each drink again. */
  cooldowns: Partial<Record<DrinkId, number>>;
}

export const EMPTY_BAR: BarState = {
  buzz: 0,
  soberProgress: 0,
  drinks: [],
  winStreak: 0,
  maxBuzz: MAX_BUZZ,
  soberEvery: SOBER_EVERY,
  slots: DRINK_SLOTS,
  priceFactor: 1,
  cooldowns: {},
};

export type Stage = 'Sober' | 'Tipsy' | 'Drunk' | 'Blackout';

export function stageOf(buzz: number, maxBuzz: number = MAX_BUZZ, tipsyAt: number = TIPSY_AT, drunkAt: number = DRUNK_AT): Stage {
  if (buzz >= maxBuzz) return 'Blackout';
  if (buzz >= drunkAt) return 'Drunk';
  if (buzz >= tipsyAt) return 'Tipsy';
  return 'Sober';
}

/** Tipsy or worse (Happy Drunk and Liquid Courage work from here). */
export function isTipsy(bar: BarState): boolean {
  return bar.buzz >= (bar.tipsyAt ?? TIPSY_AT);
}

export function isDrunk(bar: BarState): boolean {
  return bar.buzz >= (bar.drunkAt ?? DRUNK_AT);
}

/** Drinks going (each takes a slot). */
export function slotsUsed(bar: BarState): number {
  return bar.drinks.filter((d) => !drinkDef(d.id).slotless).length;
}

export function hasDrink(bar: BarState, id: DrinkId): boolean {
  return bar.drinks.some((d) => d.id === id);
}

export function buzzMeter(buzz: number, maxBuzz: number = MAX_BUZZ): string {
  return `${'▮'.repeat(Math.min(buzz, maxBuzz))}${'▯'.repeat(Math.max(0, maxBuzz - buzz))}`;
}

/** Drinks this floor's bar pours: its own plus every floor below, plus any
 *  drink whose recipe you've bought in the Gift Shop. */
export function barMenu(floor: number, recipes: readonly DrinkId[] = []): DrinkDef[] {
  return DRINKS.filter((d) => d.floor <= floor || recipes.includes(d.id));
}

/** What a drink costs right now (the Drunk bar tab included). */
export function drinkPrice(def: DrinkDef, floorMultiplier: number, bar: BarState): number {
  const base = Math.round(def.basePrice * floorMultiplier * bar.priceFactor);
  return isDrunk(bar) ? Math.ceil(base * 1.5) : base;
}

export type OrderRefusal = 'slots' | 'already' | 'money' | 'cooldown';

/** Plays until the bartender pours this drink again (0 = now). */
export function cooldownOf(bar: BarState, id: DrinkId): number {
  return bar.cooldowns?.[id] ?? 0;
}

/** Why a drink can't be ordered right now, or null if it can. */
export function orderRefusal(def: DrinkDef, bar: BarState, price: number, bankroll: number): OrderRefusal | null {
  if (price > bankroll) return 'money';
  if (def.lasts !== 'instant' && hasDrink(bar, def.id)) return 'already';
  if (cooldownOf(bar, def.id) > 0) return 'cooldown';
  if (def.lasts === 'instant') return null;
  if (!def.slotless && slotsUsed(bar) >= bar.slots) return 'slots';
  return null;
}

/** Plays a drink is sold out for after it's poured (see TUNING). */
export function restockOf(def: DrinkDef, bar?: BarState): number {
  if (TUNING.restockFactor <= 0) return TUNING.reorderCooldown;
  if (def.lasts === 'instant') return def.id === 'coffee' ? 10 : 5;
  // Booger Sugar (Gift Shop) lowers the factor for the run.
  const factor = TUNING.restockFactor - (bar?.restockCut ?? 0);
  if (def.lasts === 'once') return Math.round(5 * factor);
  return Math.round(def.lasts * factor);
}

/** Drinks the order: Buzz and a slot. Price is charged by the caller. */
export function applyOrder(bar: BarState, def: DrinkDef): BarState {
  const buzz = Math.max(0, Math.min(bar.maxBuzz, bar.buzz + def.buzz));
  const drinks = def.lasts === 'instant' ? bar.drinks : [...bar.drinks, { id: def.id, playsLeft: def.lasts === 'once' ? null : def.lasts }];
  const wait = restockOf(def, bar);
  const cooldowns = wait > 0 ? { ...bar.cooldowns, [def.id]: wait } : bar.cooldowns;
  return { ...bar, buzz, drinks, cooldowns };
}

export interface SettleEffects {
  bar: BarState;
  /** Added to the bankroll (can be negative: tips, dropped chips). */
  delta: number;
  notes: string[];
}

/**
 * Everything drinks and Buzz do when a play settles. `wagered`/`returned`
 * are the play's own totals; `dropChip` is the table minimum (for
 * Butterfingers and House Pour) and `roll` a fresh random number in [0, 1).
 */
/**
 * `countsForRestock`: whether this play brings sold-out drinks closer to
 * restocking. The casino passes false for plays below the floor's usual bet
 * (user, 2026-09-23: $1 scratchers were being used as filler to wait out
 * restocks); drink durations tick on every play regardless.
 */
export function settleWithDrinks(bar: BarState, wagered: number, returned: number, dropChip: number, roll: number, countsForRestock = true, noDealer = false): SettleEffects {
  const net = returned - wagered;
  let delta = 0;
  const notes: string[] = [];
  let drinks = bar.drinks;
  const use = (id: DrinkId) => {
    drinks = drinks.filter((d) => d.id !== id);
  };

  if (hasDrink(bar, 'pour') && net > 0) {
    const bonus = Math.max(1, Math.round(dropChip * TUNING.pour));
    delta += bonus;
    notes.push(`House Pour +$${bonus}`);
  }
  if (hasDrink(bar, 'lastcall')) {
    use('lastcall');
    if (net > 0) {
      delta += net * 2;
      notes.push(`Last Call +$${net * 2}`);
    }
  }
  if (hasDrink(bar, 'espresso') && returned > 0) {
    const bonus = Math.max(1, Math.round(returned * TUNING.espresso));
    delta += bonus;
    notes.push(`Espresso Martini +$${bonus}`);
  }
  const winStreak = net > 0 ? bar.winStreak + 1 : net < 0 ? 0 : bar.winStreak;
  if (hasDrink(bar, 'topshelf') && net > 0) {
    const bonus = Math.max(1, Math.round(net * TUNING.topShelf));
    delta += bonus;
    notes.push(`Top Shelf +$${bonus}`);
  }
  if (hasDrink(bar, 'happy') && isTipsy(bar) && returned > 0) {
    const bonus = Math.max(1, Math.round(returned * TUNING.happy));
    delta += bonus;
    notes.push(`Happy Drunk +$${bonus}`);
  }
  if (net < 0 && hasDrink(bar, 'lager')) {
    use('lager');
    delta += -net;
    notes.push(`Lucky Lager refunds $${-net}`);
  } else if (net < 0 && hasDrink(bar, 'cosmo')) {
    const back = Math.round(-net * TUNING.cosmo);
    if (back > 0) {
      delta += back;
      notes.push(`Cashback +$${back}`);
    }
  }
  if (isDrunk(bar)) {
    const winnings = net + delta;
    if (winnings > 0) {
      const tip = Math.max(1, Math.round(winnings * 0.1));
      delta -= tip;
      // A machine has no dealer to tip: the money ends up on the floor.
      notes.push(noDealer ? `Dropped $${tip} on the floor` : `Tipped the dealer $${tip}`);
    }
    if (roll < 0.15) {
      delta -= dropChip;
      notes.push(`Butterfingers: dropped $${dropChip}`);
    }
  }

  // Timed drinks tick down; one-shots wait for their moment.
  drinks = drinks
    .map((d) => (d.playsLeft === null ? d : { ...d, playsLeft: d.playsLeft - 1 }))
    .filter((d) => d.playsLeft === null || d.playsLeft > 0);
  let soberProgress = bar.soberProgress + 1;
  let buzz = bar.buzz;
  if (soberProgress >= bar.soberEvery) {
    soberProgress = 0;
    buzz = Math.max(0, buzz - 1);
  }
  const cooldowns: Partial<Record<DrinkId, number>> = {};
  for (const [id, left] of Object.entries(bar.cooldowns ?? {})) {
    const next = countsForRestock ? left! - 1 : left!;
    if (next > 0) cooldowns[id as DrinkId] = next;
  }
  return { bar: { ...bar, drinks, buzz, soberProgress, winStreak, cooldowns }, delta, notes };
}
